import mixpanel from 'mixpanel-browser'

// Global promise to track initialization state
let resolveInit
let mixpanelInitPromise = new Promise((resolve) => {
    resolveInit = resolve
})
let isMixpanelInitialized = false
let isInitStarted = false

const MIXPANEL_DISABLED_KEY = 'mixpanel_disabled'

// Read once on startup
function getMixpanelDisabled() {
    try {
        if (typeof window === 'undefined') return false
        const value = localStorage.getItem(MIXPANEL_DISABLED_KEY)
        return value === 'true' || value === '1'
    } catch (_) {
        return false
    }
}

export const initMixpanel = () => {
    if (isInitStarted) return mixpanelInitPromise
    isInitStarted = true

    // Respect user opt-out via localStorage
    if (getMixpanelDisabled()) {
        resolveInit(false)
        return mixpanelInitPromise
    }

    try {
        if (mixpanel && import.meta.env.VITE_APP_MIXPANEL_TOKEN) {
            mixpanel.init(`${import.meta.env.VITE_APP_MIXPANEL_TOKEN}`, {
                debug: false,
                track_marketing: true,
                autocapture: {
                    pageview: false,
                    click: false,
                    dead_click: true,
                    rage_click: true,
                    scroll: false,
                    submit: false,
                    capture_text_content: false,
                },
                loaded: (mp) => {
                    isMixpanelInitialized = true
                    resolveInit(true)
                },
            })
            window.mixpanel = mixpanel

            // Mark mixpanel as initialized to allow safe resets elsewhere
            try {
                window.__mixpanelInited = true
            } catch (_) {}

            // Capture non-UTM arrival params (invite/coupon) on first touch
            try {
                const params = new URLSearchParams(window.location.search)
                const invite = params.get('invite')
                const coupon = params.get('code')

                const superProps = {}
                if (invite) superProps.invite = invite
                if (coupon) superProps.coupon = coupon

                if (Object.keys(superProps).length) {
                    mp.register_once(superProps)
                    mp.track('Landing Context', superProps)
                }
            } catch (_) {}

            // Capture UTM parameters and set as persistent user properties (first-touch attribution)
            try {
                const params = new URLSearchParams(window.location.search)
                const utmParams = [
                    'utm_source',
                    'utm_medium',
                    'utm_campaign',
                    'utm_content',
                    'utm_term',
                ]

                const utmProperties = {}
                utmParams.forEach((param) => {
                    const value = params.get(param)
                    if (value) {
                        utmProperties[param] = value
                    }
                })

                if (Object.keys(utmProperties).length > 0) {
                    mp.people.set_once(utmProperties)
                }
            } catch (_) {}
        } else {
            resolveInit(false)
        }
    } catch (error) {
        console.error('Mixpanel init failed:', error)
        resolveInit(false)
    }

    return mixpanelInitPromise
}

export const trackEvent = async (event, properties = {}) => {
    try {
        // Wait for initialization if it's pending
        if (mixpanelInitPromise && !isMixpanelInitialized) {
            await Promise.race([
                mixpanelInitPromise,
                new Promise((r) => setTimeout(r, 2000)), // 2s timeout
            ])
        }

        // Respect user opt-out via localStorage or global flag
        const isDisabled = (() => {
            try {
                if (typeof window === 'undefined') return false
                if (window.__mixpanelDisabled) return true
                return getMixpanelDisabled()
            } catch (_) {
                return false
            }
        })()
        if (isDisabled) return true

        const base = import.meta.env.VITE_API_URL || ''
        // Prefer authenticated endpoint when user is logged in so server can include $user_id
        const preferAuth = !!window?.firebase?.auth?.().currentUser
        const url = base.endsWith('/')
            ? `${base}track${preferAuth ? '/auth' : ''}`
            : `${base}/track${preferAuth ? '/auth' : ''}`


        // Try to get Mixpanel distinct_id to unify backend/frontend tracking
        let mixpanel_distinct_id = null
        try {
            if (typeof window !== 'undefined' && window.mixpanel) {
                mixpanel_distinct_id = window.mixpanel.get_distinct_id()
            }
        } catch (_) {}

        const payload = JSON.stringify({
            event,
            properties: {
                ...properties,
                ...(mixpanel_distinct_id && { mixpanel_distinct_id }),
            },
        })

        // Prefer sendBeacon on page-hide/unload, but only same-origin to include cookies
        if (
            typeof window !== 'undefined' &&
            typeof navigator !== 'undefined' &&
            navigator.sendBeacon &&
            document.visibilityState === 'hidden'
        ) {
            const sameOrigin =
                new URL(url, window.location.origin).origin ===
                window.location.origin
            if (sameOrigin) {
                const blob = new Blob([payload], { type: 'application/json' })
                const ok = navigator.sendBeacon(url, blob)
                if (ok) return true
                // fall through to fetch if beacon failed
            }
        }

        const fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: payload,
            credentials: 'include',
            keepalive: true,
            mode: 'cors',
        }

        // If authenticated, attach idtoken header so backend can enrich with $user_id
        try {
            const user = window?.firebase?.auth?.().currentUser
            if (preferAuth && user) {
                const token = await user.getIdToken()
                fetchOptions.headers = {
                    ...fetchOptions.headers,
                    idtoken: token,
                }
            }
        } catch (_) {}

        await fetch(url, fetchOptions)
        return true
    } catch (error) {
        if (import.meta.env.DEV) {
            console.debug('Error tracking event:', error)
        }
        return false
    }
}

const FIRST_ACTIVITY_KEY = 'has_tracked_first_activity'

/**
 * Sets up listeners to track the very first user activity (click, scroll, or keydown).
 * This event is tracked only once per browser (persisted in localStorage).
 */
export const setupFirstActivityTracking = () => {
    // 1. Check if already tracked
    try {
        if (localStorage.getItem(FIRST_ACTIVITY_KEY)) return
    } catch (_) {
        return // localStorage access might fail in some contexts
    }

    let isSending = false

    // 2. Define handler
    const handleActivity = async (event) => {
        try {
            // Check again to avoid race conditions if multiple events fire aggressively
            if (localStorage.getItem(FIRST_ACTIVITY_KEY)) return

            if (isSending) return
            isSending = true

            // Track the event
            const success = await trackEvent('First Activity', {
                type: event.type,
            })

            isSending = false

            if (success) {
                // Mark as tracked immediately to prevent double-firing
                localStorage.setItem(FIRST_ACTIVITY_KEY, 'true')

                // Remove all listeners
                cleanup()
            }
        } catch (error) {
            isSending = false
            console.error('Error handling first activity:', error)
        }
    }

    const cleanup = () => {
        window.removeEventListener('click', handleActivity)
        window.removeEventListener('scroll', handleActivity)
        window.removeEventListener('keydown', handleActivity)
        window.removeEventListener('touchstart', handleActivity)
    }

    // 3. Add listeners
    // Use capture: true to catch events before they are stopped by UI components
    const options = { capture: true, passive: true }
    window.addEventListener('click', handleActivity, options)
    window.addEventListener('scroll', handleActivity, options)
    window.addEventListener('keydown', handleActivity, options)
    window.addEventListener('touchstart', handleActivity, options)
}

export default {
    trackEvent,
    setupFirstActivityTracking,
    initMixpanel,
}

